import React, { useState, useCallback, useReducer, Suspense } from "react";
import { AppContext } from "./appContext";
import LoaderSpinner from "./components/LoaderSpinner";
import LoaderSpeechmark from "./components/LoaderSpeechmark";
import {
  PageData,
  GlobalData,
  ActivePage,
  setMockPageData
} from "./serverData";
import pageList, { shouldUseRouter } from "./pageList";
import { BrowserRouter } from "react-router-dom";
import { Global, css } from "@emotion/core";
import { CookiesProvider } from "react-cookie";
import loaders from "./loaders";

// global styles
import globalOverrideStyles from "styling/globalOverrides";
import animStyles from "styling/animations";

const translationsReducer = (state, action) => {
  return action.data;
};

const appOverrideStyles = `
/* for position sticky to work */
.content { overflow: visible }
`;

const App = props => {
  const [overlayLoaderVisible, setOverlayLoaderVisible] = useState(false);
  const [pagePreloaderVisible, setPagePreloaderVisible] = useState(true);
  const [pageKey, setPageKey] = useState("initial-page-key");

  const [translations, setTranslations] = useReducer(translationsReducer, null);

  const globalTranslations = GlobalData.translations;

  const showOverlayLoader = useCallback(() => {
    setOverlayLoaderVisible(true);
  }, []);

  const hideOverlayLoader = useCallback(() => {
    setOverlayLoaderVisible(false);
  }, []);

  const showPagePreloader = useCallback(() => {
    setPagePreloaderVisible(true);
  }, []);

  const hidePagePreloader = useCallback(() => {
    setPagePreloaderVisible(false);
  }, []);

  const refreshPage = useCallback(() => {
    setPageKey(+new Date());
    window.scrollTo(0, 0);
  }, []);

  const contextValue = {
    showOverlayLoader: showOverlayLoader,
    hideOverlayLoader: hideOverlayLoader,
    showPagePreloader: showPagePreloader,
    hidePagePreloader: hidePagePreloader,
    setTranslations: setTranslations,
    translations: translations,
    refreshPage: refreshPage
  };

  const renderRouter = children => {
    // enable router only if not in storybook (pageName prop is only provided by storybook)
    // and only if page requires it
    const enableRouter = !props.pageName && shouldUseRouter(ActivePage);
    if (!enableRouter) return children;
    return <BrowserRouter>{children}</BrowserRouter>;
  };

  const renderPage = () => {
    const PageTag = pageList[props.pageName || ActivePage];

    return (
      <>
        <Global styles={css(globalOverrideStyles)} />
        <Global styles={css(appOverrideStyles)} />
        <Global styles={css(animStyles)} />

        <AppContext.Provider value={contextValue}>
          {renderRouter(
            <CookiesProvider>
              <Suspense fallback={null}>
                <PageTag key={pageKey} />
              </Suspense>
            </CookiesProvider>
          )}
        </AppContext.Provider>
      </>
    );
  };

  const renderPagePreloader = () => {
    let CustomLoader = null;
    if (PageData.preloader) {
      CustomLoader = loaders[PageData.preloader.name];
    }

    return (
      <>
        {CustomLoader ? (
          <Suspense fallback={null}>
            <CustomLoader
              mode="sticky"
              isLoading={pagePreloaderVisible}
              height={"100%"}
            />
          </Suspense>
        ) : (
          // default
          <LoaderSpeechmark mode="sticky" isLoading={pagePreloaderVisible} />
        )}
      </>
    );
  };
  // for storybook
  if (props.pageName) setMockPageData(props.pageName);
  return (
    <>
      {/* render page before loaders to support custom page headers while loading */}
      {renderPage()}
      {renderPagePreloader()}
      <LoaderSpinner
        mode="overlay"
        isLoading={overlayLoaderVisible}
        message={globalTranslations.overlayLoaderMessage} //todo: ability to display en message (switch global data per lang)
        color="gray-dark"
      />
    </>
  );
};

export default App;
